import { AutocompleteInput } from '@ipfabric/ui-components/AutocompleteInput/AutocompleteInput';
import { Chip } from '@ipfabric/ui-components/Chip/Chip';
import { FormField } from '@ipfabric/ui-components/FormField/FormField';
import { useMemo, useState } from 'react';
import type { SelectedAssignmentTarget } from '../../assignedLabelsTypes';
import {
  dedupeTargets,
  formatTargetChipLabel,
  getTargetKey,
} from '../../targetIdentity';
import { TargetSearchMenuHeader } from './TargetSearchMenuHeader';
import { TargetSearchOptionRow } from './TargetSearchOptionRow';
import { buildTargetDropdownOptions } from './buildTargetDropdownOptions';
import type { TargetDropdownOption } from './buildTargetDropdownOptions';
import { useLabelAssignmentTargetSearch } from './useLabelAssignmentTargetSearch';

type TargetFieldProps = {
  error?: string;
  onChange: (targets: SelectedAssignmentTarget[]) => void;
  value: SelectedAssignmentTarget[];
};

export const TargetField = ({ error, onChange, value }: TargetFieldProps) => {
  const [showInterfaces, setShowInterfaces] = useState(false);
  const { inputValue, isLoading, parsedInput, results, setInputValue } =
    useLabelAssignmentTargetSearch();

  const dropdownOptions = useMemo(
    () =>
      buildTargetDropdownOptions(
        results,
        showInterfaces,
        parsedInput.interfaceFilter,
      ),
    [parsedInput.interfaceFilter, results, showInterfaces],
  );

  const handleOptionSelect = (option: TargetDropdownOption) => {
    if (!option.selectable || !option.target) {
      return;
    }

    onChange(dedupeTargets([...value, option.target]));
    setInputValue('');
  };

  const removeTarget = (target: SelectedAssignmentTarget) => {
    const key = getTargetKey(target);
    onChange(value.filter((item) => getTargetKey(item) !== key));
  };

  const getChipRemoveTestId = (target: SelectedAssignmentTarget) =>
    `assign-labels-target-chip-remove-${target.sn}`;

  return (
    <FormField
      description='Lookup any on-prem device or cloud object'
      error={error}
      htmlFor='assign-labels-target-input'
      label='Target'
    >
      <AutocompleteInput.Record
        dataTestId='assign-labels-target-input'
        editableWithTags
        getOptionKey={(option) => option.key}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) => option.label === val}
        loading={isLoading}
        onChange={setInputValue}
        onClear={() => setInputValue('')}
        onOptionSelect={handleOptionSelect}
        onSearch={async () => ({
          content: dropdownOptions,
          currentPage: 0,
          paginationLimit: dropdownOptions.length,
          totalPageCount: 1,
          totalRowsCount: dropdownOptions.length,
        })}
        options={dropdownOptions}
        placeholder='Serial number or hostname'
        renderMenuHeader={
          <TargetSearchMenuHeader
            onChange={setShowInterfaces}
            showInterfaces={showInterfaces}
          />
        }
        renderOption={(option) => (
          <TargetSearchOptionRow
            badgeLabel={option.badgeLabel}
            dataTestId={option.dataTestId}
            indented={option.indented}
            kind={option.kind}
            primaryText={option.primaryText}
            secondaryText={option.secondaryText}
          />
        )}
        renderTags={
          value.length > 0
            ? () =>
                value.map((target) => (
                  <Chip
                    key={getTargetKey(target)}
                    dataTestId={getChipRemoveTestId(target)}
                    onRemove={() => removeTarget(target)}
                    size='small'
                    variant='outline'
                  >
                    {formatTargetChipLabel(target)}
                  </Chip>
                ))
            : undefined
        }
        value={inputValue}
      />
    </FormField>
  );
};
